// src/components/inventory/ProductParsingPanel.js
import React, { useState, useEffect } from 'react';
import { 
  Wand2, 
  X,
  Package,
  Hash,
  RefreshCw,
  Eye,
  AlertCircle
} from 'lucide-react';

const ProductParsingPanel = ({ 
  product, 
  onApplyFix,
  onCancel,
  onAnalyzeAnother,
  showMultiple = false 
}) => {
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [fieldAssignments, setFieldAssignments] = useState({});
  const [updateName, setUpdateName] = useState(false);

  const parseProductName = (productName) => {
    if (!productName || typeof productName !== 'string') {
      return { 
        name: productName || '', 
        cleanName: productName || '',
        detectedElements: []
      };
    }

    const originalName = productName.trim();
    const detectedElements = [];
    
    // Helper function to find and extract patterns
    const findPatterns = () => {
      const text = originalName;
      
      // Pattern 1: Weight range with slash and count (4-5oz/10)
      const weightRangeWithCount = /(\d+-\d+oz\s*\/\s*\d+)/gi;
      let match;
      while ((match = weightRangeWithCount.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'packSize',
          description: 'Weight range with count',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'high'
        });
      }
      
      // Pattern 2: Weight range with count and type (4-5oz/10FCL)
      const weightRangeWithType = /(\d+-\d+oz\s*\/\s*\d+FCL)/gi;
      while ((match = weightRangeWithType.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'packSize',
          description: 'Weight range with case type',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'high'
        });
      }
      
      // Pattern 3: Decimal weight range with count (.5-1kg/10KG)
      const decimalWeightWithCount = /(\.\d+-\d+kg\s*\/\s*\d+KG)/gi;
      while ((match = decimalWeightWithCount.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'packSize',
          description: 'Decimal weight with case',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'high'
        });
      }
      
      // Pattern 4: Simple weight range (4-5oz, 8-10oz)
      const simpleWeightRange = /(\d+-\d+oz)/gi;
      while ((match = simpleWeightRange.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'packSize',
          description: 'Weight range',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'medium'
        });
      }
      
      // Pattern 5: Single weight (8oz, 500g)
      const singleWeight = /(\d+(?:\.\d+)?\s*(?:oz|g|kg|lb|ml))/gi;
      while ((match = singleWeight.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'packSize',
          description: 'Single weight',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'medium'
        });
      }
      
      // Pattern 6: Categories (WhlRnd, FltSkon, Btfly)
      const categories = /\b(WhlRnd|FltSkon|Btfly|Fillet|Steak|Diced|Whole|Fresh|Frozen)\b/gi;
      while ((match = categories.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'category',
          description: 'Product type',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'medium'
        });
      }
      
      // Pattern 7: SKU patterns
      const skuPatterns = [
        /([A-Z]{2,}\d{3,}(?:-\d+)?)/g,  // AB1234, PROD-001
        /(#\s*[A-Z0-9]{4,})/g           // #ABC123
      ];
      
      skuPatterns.forEach(pattern => {
        while ((match = pattern.exec(text)) !== null) {
          detectedElements.push({
            value: match[1],
            type: 'sku',
            description: 'SKU code',
            startIndex: match.index,
            endIndex: match.index + match[1].length,
            confidence: 'medium'
          });
        }
      });
      
      // Pattern 8: Countries
      const countries = /\b(USA|US|China|CN|India|IN|Japan|JP|Germany|DE|UK|Canada|CA|Australia|AU|Brazil|BR)\b/gi;
      while ((match = countries.exec(text)) !== null) {
        detectedElements.push({
          value: match[1],
          type: 'country',
          description: 'Country of origin',
          startIndex: match.index,
          endIndex: match.index + match[1].length,
          confidence: 'low'
        });
      }
    };
    
    findPatterns();
    
    // Remove overlapping elements (keep the longest match)
    detectedElements.sort((a, b) => a.startIndex - b.startIndex);
    const filteredElements = [];
    let lastEnd = -1;
    
    for (const el of detectedElements) {
      if (el.startIndex >= lastEnd) {
        filteredElements.push(el);
        lastEnd = el.endIndex;
      } else {
        // Overlap - keep the longer element
        const prevEl = filteredElements[filteredElements.length - 1];
        const prevLength = prevEl.endIndex - prevEl.startIndex;
        const currLength = el.endIndex - el.startIndex;
        
        if (currLength > prevLength) {
          filteredElements[filteredElements.length - 1] = el;
          lastEnd = el.endIndex;
        }
      }
    }
    
    // Build clean name
    let cleanName = originalName;
    filteredElements.forEach(el => {
      const elementText = originalName.substring(el.startIndex, el.endIndex);
      cleanName = cleanName.replace(elementText, ' ');
    });
    
    // Clean up
    cleanName = cleanName
      .replace(/\s+/g, ' ')
      .replace(/\s+[\\/]\s*$/, '')  // Remove trailing slashes with spaces
      .replace(/\s*,\s*$/, '')
      .replace(/\s*\.\s*$/, '')
      .trim();
    
    return {
      originalName,
      cleanName: cleanName || originalName,
      detectedElements: filteredElements,
      changed: cleanName !== originalName
    };
  };

  const applyProductFixes = (product, assignments, shouldUpdateName) => {
    let updated = { ...product };
    const parsed = parseProductName(product.name || '');
    
    // Only update name if checkbox is checked
    if (shouldUpdateName) {
      updated.name = parsed.cleanName;
    }
    
    // ALWAYS apply field assignments regardless of name update
    Object.keys(assignments).forEach(elementValue => {
      const assignment = assignments[elementValue];
      if (assignment !== 'ignore') {
        updated[assignment] = elementValue;
      }
    });
    
    return updated;
  };
  
  useEffect(() => {
    if (product) {
      setIsAnalyzing(true);
      setTimeout(() => {
        const parsed = parseProductName(product.name || '');
        
        // Initialize field assignments
        const initialAssignments = {};
        parsed.detectedElements.forEach(el => {
          if (el.type === 'packSize' && !product.packSize) {
            initialAssignments[el.value] = 'packSize';
          } else if (el.type === 'category' && !product.category) {
            initialAssignments[el.value] = 'category';
          } else if (el.type === 'country' && !product.countryOfOrigin) {
            initialAssignments[el.value] = 'countryOfOrigin';
          } else if (el.type === 'sku' && !product.sku) {
            initialAssignments[el.value] = 'sku';
          } else {
            initialAssignments[el.value] = 'ignore';
          }
        });
        
        setFieldAssignments(initialAssignments);
        setAnalysis({ parsed });
        setIsAnalyzing(false);
      }, 100);
    }
  }, [product]);
  
  const handleAssignmentChange = (elementValue, field) => {
    setFieldAssignments(prev => ({
      ...prev,
      [elementValue]: field
    }));
  };
  
  const handleApply = () => {
    if (!product || !analysis) return;
    
    const fixedProduct = applyProductFixes(product, fieldAssignments, updateName);
    onApplyFix(fixedProduct);
  };
  
  if (!product) return null;
  
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-sm max-w-md mx-auto">
      {/* Header - Compact */}
      <div className="flex items-center justify-between p-2 border-b">
        <div className="flex items-center gap-1.5">
          <div className="p-0.5 bg-purple-100 rounded">
            <Wand2 className="w-3.5 h-3.5 text-purple-600" />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-gray-900 leading-tight">Product Name Parser</h3>
            <p className="text-xs text-gray-500 leading-tight">
              {showMultiple ? 'Batch processing' : 'Smart name cleanup'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          {showMultiple && onAnalyzeAnother && (
            <button
              onClick={onAnalyzeAnother}
              className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title="Analyze another"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={onCancel}
            className="p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
      
      {/* Current Product Info - Compact */}
      <div className="p-2 border-b">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0 pr-2">
            <p className="text-xs font-medium text-gray-900 truncate">{product.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              {product.sku && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                  <Hash className="w-2.5 h-2.5" />
                  {product.sku}
                </span>
              )}
              {product.packSize && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                  <Package className="w-2.5 h-2.5" />
                  {product.packSize}
                </span>
              )}
            </div>
          </div>
          {isAnalyzing && (
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600 mt-1"></div>
          )}
        </div>
      </div>
      
      {isAnalyzing ? (
        <div className="p-4 text-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-xs text-gray-500 mt-1">Analyzing patterns...</p>
        </div>
      ) : analysis ? (
        <div className="p-2 space-y-2">
          {/* Name Update Toggle */}
          <label className="flex items-center gap-1.5 p-1.5 border border-gray-300 rounded cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={updateName}
              onChange={(e) => setUpdateName(e.target.checked)}
              className="h-3 w-3 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
            />
            <div>
              <p className="text-xs font-medium text-gray-900">Update product name to clean version</p>
              <p className="text-xs text-gray-500">Uncheck to keep original name</p>
            </div>
          </label>
          
          {/* Name Preview (when checkbox is checked) */}
          {updateName && analysis.parsed.originalName !== analysis.parsed.cleanName && (
            <div className="p-1.5 border border-blue-200 rounded bg-blue-50">
              <div className="flex items-start gap-1.5">
                <Eye className="w-3 h-3 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-blue-800 mb-0.5">Name will change to:</p>
                  <div className="text-xs">
                    <div className="text-gray-500 line-through truncate">{analysis.parsed.originalName}</div>
                    <div className="text-green-700 font-medium truncate">{analysis.parsed.cleanName}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Detected Elements */}
          {analysis.parsed.detectedElements.length > 0 ? (
            <div className="border border-gray-300 rounded overflow-hidden">
              <div className="px-2 py-1.5 bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-medium text-gray-900">Detected Elements</p>
                  <p className="text-xs text-gray-500">Assign to fields below</p>
                </div>
              </div>
              
              <div className="p-1.5 space-y-1 max-h-32 overflow-y-auto">
                {analysis.parsed.detectedElements.map((el, idx) => (
                  <div key={idx} className="p-1.5 border border-gray-200 rounded hover:bg-gray-50">
                    <div className="flex items-start justify-between gap-1.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1 mb-0.5">
                          <span className="text-xs font-medium text-gray-900 truncate">"{el.value}"</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            el.type === 'packSize' ? 'bg-blue-100 text-blue-700' :
                            el.type === 'category' ? 'bg-green-100 text-green-700' :
                            el.type === 'sku' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {el.type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">{el.description}</p>
                      </div>
                      
                      <div className="flex-shrink-0">
                        <select
                          value={fieldAssignments[el.value] || 'ignore'}
                          onChange={(e) => handleAssignmentChange(el.value, e.target.value)}
                          className="text-xs border border-gray-300 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-purple-500 min-w-[100px]"
                        >
                          <option value="ignore">Ignore</option>
                          <option value="packSize">Pack Size</option>
                          <option value="sku">SKU</option>
                          <option value="category">Category</option>
                          <option value="countryOfOrigin">Origin</option>
                        </select>
                      </div>
                    </div>
                    
                    {fieldAssignments[el.value] !== 'ignore' && (
                      <div className="mt-1 pt-1 border-t text-xs">
                        <span className="text-gray-500">Will save to: </span>
                        <span className="font-medium capitalize">{fieldAssignments[el.value]}</span>
                        {product[fieldAssignments[el.value]] && product[fieldAssignments[el.value]] !== el.value && (
                          <span className="text-gray-500 ml-1">
                            (replaces: {product[fieldAssignments[el.value]]})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-2 text-center border border-gray-200 rounded">
              <AlertCircle className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-xs text-gray-500">No elements detected in product name</p>
            </div>
          )}
          
          {/* Summary Preview - Compact Grid */}
          <div className="border border-gray-300 rounded p-1.5">
            <p className="text-xs font-medium text-gray-700 mb-1">Summary Preview:</p>
            <div className="grid grid-cols-3 gap-1">
              {/* Name */}
              <div className="bg-white p-1.5 border border-gray-200 rounded">
                <div className="text-xs font-medium text-gray-500 mb-0.5">Name</div>
                <div className={`text-xs font-medium truncate ${
                  updateName && analysis.parsed.changed ? 'text-green-600' : 'text-gray-600'
                }`}>
                  {updateName ? analysis.parsed.cleanName : "(unchanged)"}
                </div>
              </div>
              
              {/* Pack Size */}
              <div className="bg-white p-1.5 border border-gray-200 rounded">
                <div className="text-xs font-medium text-gray-500 mb-0.5">Pack Size</div>
                <div className="text-xs font-medium text-blue-600 truncate">
                  {Object.entries(fieldAssignments).find(([_, f]) => f === 'packSize')?.[0] || "(none)"}
                </div>
              </div>
              
              {/* Category */}
              <div className="bg-white p-1.5 border border-gray-200 rounded">
                <div className="text-xs font-medium text-gray-500 mb-0.5">Category</div>
                <div className="text-xs font-medium text-green-600 truncate">
                  {Object.entries(fieldAssignments).find(([_, f]) => f === 'category')?.[0] || "(none)"}
                </div>
              </div>
            </div>
            
            {/* Other fields summary */}
            <div className="mt-1.5 pt-1.5 border-t text-xs">
              {Object.entries(fieldAssignments)
                .filter(([_, field]) => field === 'sku' || field === 'countryOfOrigin')
                .map(([value, field]) => (
                  <div key={value} className="flex items-center justify-between">
                    <span className="text-gray-500 capitalize">{field}:</span>
                    <span className={`font-medium ${
                      field === 'sku' ? 'text-purple-600' : 'text-orange-600'
                    }`}>
                      {value}
                    </span>
                  </div>
                ))}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center justify-between pt-1.5 border-t">
            <div className="text-xs text-gray-500">
              {Object.values(fieldAssignments).filter(v => v !== 'ignore').length > 0 ? (
                <span className="font-medium text-purple-700">
                  {Object.values(fieldAssignments).filter(v => v !== 'ignore').length} field(s) will update
                  {updateName && analysis.parsed.changed && " + name"}
                </span>
              ) : (
                <span className="text-gray-500">No changes selected</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={onCancel}
                className="px-2.5 py-1 text-xs border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                disabled={Object.values(fieldAssignments).every(v => v === 'ignore') && (!updateName || !analysis.parsed.changed)}
                className="px-2.5 py-1 text-xs bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 text-center">
          <p className="text-xs text-gray-500">Unable to analyze product</p>
        </div>
      )}
    </div>
  );
};

export default ProductParsingPanel;
